import { useCallback, useContext } from 'react'
import { Pattern } from '../app/builder/contexts/PatternContext'
import { useApplicationSuiteContext } from '../contexts/ApplicationSuiteProvider'
import { SnackbarHelperContext } from '../contexts/SnackbarHelperContext'
import { useSavePattern } from './useSavePattern'
import { copyPatternToAccount } from '../common/utils/copyPatternToAccount'

type ForceSave = (pattern: Pattern) => Promise<Pattern | undefined>

// Copies a pattern into the user's account with the caller's existing forceSave and opens the
// "pattern copied" dialog on success. Use this when the component already has a useSavePattern.
export function useCopyPatternWithSave(forceSave: ForceSave) {
  const { setCopiedPattern } = useApplicationSuiteContext()
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const copyPattern = useCallback(
    async (pattern: Pattern, originalGcid?: string): Promise<Pattern | undefined> => {
      try {
        setSnackbarSettings({
          message: 'Copying pattern to your account...',
          severity: 'info',
          autoHideDuration: 3000,
        })

        const savedPattern = await copyPatternToAccount(pattern, forceSave, originalGcid)

        if (savedPattern) {
          // The dialog is the success signal; drop the save snackbar so they don't stack
          setSnackbarSettings(null)
          setCopiedPattern(savedPattern)
        }

        return savedPattern
      } catch (error) {
        console.error('Error copying pattern:', error)
        setSnackbarSettings({
          message: 'Error copying pattern',
          severity: 'error',
          autoHideDuration: 6000,
        })
        return undefined
      }
    },
    [forceSave, setCopiedPattern, setSnackbarSettings]
  )

  return { copyPattern }
}

// Same as above for components that don't otherwise save patterns
export function useCopyPatternToAccount() {
  const { usermeta, tradingHouse, canViewPrivatePatterns } = useApplicationSuiteContext()
  const { forceSave } = useSavePattern(() => {}, usermeta, tradingHouse, canViewPrivatePatterns)
  return useCopyPatternWithSave(forceSave)
}
